(ns admin.components.composite.question-editor.core
  (:require
   [re-frame.core :as re-frame]
   [reagent.core :as reagent]
   [reagent-form.core :as rf]
   [potpuri.core :as pot]

   [kyber.components.base.button :as button]
   [kyber.components.base.text :as text]
   [kyber.components.composite.resource :as resource]
   [kyber.components.composite.loading :as loading]
   [kyber.styles.core :refer [gs styles->classes]]
   [kyber.lib.kyber-db :as kyber-db]

   [admin.events.questions :as >questions]

   [admin.components.composite.question-editor.feedback-field
    :as feedback-field]
   [admin.utils :as au]
   [admin.components.composite.resource-uploader :as resource-uploader]
   [admin.components.composite.resource-manager :as resource-manager]
   [admin.components.composite.step-navigation :as step-navigation]
   [admin.components.composite.question-editor.single-answer
    :as single-answer]
   [admin.components.composite.question-editor.multiple-answer
    :as multiple-answer]
   [admin.components.composite.question-editor.rating-scale
    :as rating-scale]
   [admin.components.composite.question-editor.free-response
    :as free-response]
   [admin.components.composite.question-editor.question-settings
    :as question-settings]))

(def classes
  (styles->classes
   {:question-editor-container
    {:width "100%"
     :box-shadow (gs [:shadows :shadow-12])}

    :step-navigation-container
    {:padding (gs [:spacing :p4])
     :display :flex
     :justify-content :space-between
     :align-items :center
     :border-top (str "1px solid " (gs [:colors :gray-scale-100]))
     :border-bottom (str "1px solid " (gs [:colors :gray-scale-100]))
     :margin-bottom (gs [:spacing :p8])}

    :step-navigation-step-container
    {:display :flex
     :align-items :center
     :margin [(gs [:spacing :p20])
              (gs [:spacing :p0])]}

    :step-container
    {:padding [[(gs [:spacing :p0])
                (gs [:spacing :p16])
                (gs [:spacing :p16])
                (gs [:spacing :p16])]]}

    :active-step-navigation-step-container
    {:border-bottom (str "2px solid " (gs [:colors :primary-lite-600]))}

    :step-icon
    {:margin-right (gs [:spacing :p12])
     :color (gs [:colors :chart-orange])}

    :complete-step-icon
    {:color (gs [:colors :chart-green])}

    :info-step-icon
    {:color (gs [:colors :gray-scale-400])
     ;; NOTE This accounts for the missing step count margin
     :margin-top (gs [:spacing :p12])
     :margin-bottom (gs [:spacing :p12])}

    :disabled-step-icon
    {:color (gs [:colors :gray-scale-300])}

    :cta-container
    {:display :flex
     :align-items :flex-end
     :justify-content :space-between}

    :resource-management-container
    {:margin [(gs [:spacing :p20])
              (gs [:spacing :p0])]
     :padding-top (gs [:spacing :p20])
     :border-top (str "1px solid " (gs [:colors :gray-scale-100]))}

    :subject-matter-container
    {:background-color (gs [:colors :gray-scale-50])
     :margin [(gs [:spacing :p20])
              (gs [:spacing :p0])]
     :text-align :center
     :height "200px"
     :position :relative}

    :subject-matter-loading
    {:position :absolute
     :left "50%"
     :transform "translateY(50%)"}

    :subject-matter-object-image
    {:display :inline-block
     :position :relative
     :z-index 1
     :margin "0 auto"
     :width :auto
     :height "200px"}}))

(defn- pause-and-close
  [show-form]
  (js/setTimeout
   #(reset! show-form false)
   1000))

(defn- render-file-uploader
  [_]
  (let [upload-new (reagent/atom false)]
    (fn [{:keys [curriculum-uuid
                 activity-uuid
                 question-uuid
                 question-context
                 tmp-subject-matter-object]}]
      (let [subject-matter-object-url
            (or (get-in question-context
                        [:question.context/question
                         :question/subject-matter-object
                         :resource/url])
                (:resource/url @tmp-subject-matter-object))]
        [:div {:class (:file-uploader-container classes)}
         (if (true? @upload-new)
           [resource-uploader/render-inline-uploader
            {:activity-uuid activity-uuid
             :on-success #(let [pause-and-close (fn [] (pause-and-close upload-new))]
                            (if (some? question-uuid)
                              (re-frame/dispatch
                               [::>questions/update-question
                                {:curriculum/uuid curriculum-uuid
                                 :activity/uuid activity-uuid
                                 :question/uuid question-uuid
                                 :question/subject-matter-object {:resource/uuid (:resource/uuid %)}}

                                {:response-keys [:question/uuid
                                                 {:question/subject-matter-object
                                                  (kyber-db/get-model-keys :resource)}]
                                 :on-success pause-and-close}])
                              (do
                                (reset! tmp-subject-matter-object %)
                                (pause-and-close))))
             :on-cancel #(reset! upload-new false)
             :public-access false
             :accepted-file-types #{:image}}]
           [:div
            (if (some? subject-matter-object-url)
              [:div
               [:div {:class (:subject-matter-container classes)}
                [:div {:class (:subject-matter-loading classes)}
                 [loading/circles]]
                [:img {:class (:subject-matter-object-image classes)
                       :src (resource/get-source subject-matter-object-url)}]]
               [button/secondary-button
                {:on-click #(if (some? question-uuid)
                              (re-frame/dispatch
                               [::>questions/update-question
                                {:curriculum/uuid curriculum-uuid
                                 :activity/uuid activity-uuid
                                 :question/uuid question-uuid
                                 :question/subject-matter-object nil}
                                {:response-keys [:question/uuid
                                                 :question/subject-matter-object]
                                 :on-success-toast "Image Removed"}])
                              (reset! tmp-subject-matter-object nil))}
                "Remove Image"]]
              [button/primary-button
               {:style {:margin-top (gs [:spacing :p20])}
                :on-click #(reset! upload-new true)}
               "Upload Image"])])]))))

(defn- should-show-display-settings?
  [{:keys [question-context questionnaire-type can-adjust-requiredness]}]
  (let [has-correctness (-> question-context :question.context/question :question/has-correctness?)]
    (or (true? (= questionnaire-type :moment))
        (and (false? has-correctness)
             (true? can-adjust-requiredness)))))

(defn- persist-question
  [question-type activity-uuid curriculum-uuid on-question-submit tmp-subject-matter-object question]
  (let [is-new (nil? (:question/uuid question))
        dispatch-key (if is-new
                       ::>questions/create-question
                       ::>questions/update-question)]
    (re-frame/dispatch
     [dispatch-key
      (cond-> question
        :always
        (assoc :question/type (name question-type)
               :activity/uuid activity-uuid
               :curriculum/uuid curriculum-uuid)
        :always
        (dissoc :question.context/learning-objective :question.context/confidence)

        (and is-new
             (some? @tmp-subject-matter-object))
        (assoc :question/subject-matter-object
               (select-keys @tmp-subject-matter-object [:resource/uuid])))
      {:response-keys [:question/uuid
                       :question/type
                       :question/body
                       {:question/choices [:question.choice/body
                                           :question.choice/correct-answer
                                           :question.choice/uuid]}
                       {:question/headers [:question.header/uuid
                                           :question.header/body
                                           :question.header/index]}
                       :question/created-at
                       :question/key?
                       :question/other-enabled
                       :question/has-correctness?
                       :question/educator-feedback
                       {:question/subject-matter-object
                        (kyber-db/get-model-keys :resource)}]
       :on-success (partial on-question-submit
                            {:learning-objective (:question.context/learning-objective question)
                             :confidence (:question.context/confidence-enabled? question)})}])))

(defn- render-question-step
  [{:keys [activity-uuid curriculum-uuid questionnaire-type]}]
  (let [activity (re-frame/subscribe [:activities/activity
                                      {:curriculum-uuid curriculum-uuid
                                       :activity-uuid activity-uuid}])]

    (fn [{:keys [activity-uuid
                 question-context
                 on-question-submit
                 on-cancel
                 is-updating
                 hide-educator-feedback
                 learning-objectives]}]
      (let [{:keys [allow-disabling-of-cbqs]
             :or {allow-disabling-of-cbqs false}}
            @activity

            on-cancel*
            (fn [e]
              (.preventDefault e)
              (on-cancel))

            question-uuid
            (-> (:question.context/question question-context)
                :question/uuid)

            learning-objective
            (:question.context/learning-objective question-context)

            confidence-enabled
            (:question.context/confidence-enabled? question-context)

            tmp-subject-matter-object
            (reagent/atom nil)

            shared-payload
            {:question (:question.context/question question-context)
             :confidence-enabled confidence-enabled
             :hide-educator-feedback hide-educator-feedback
             :learning-objectives learning-objectives
             :learning-objective learning-objective
             :allow-disabling-of-cbqs allow-disabling-of-cbqs
             :on-cancel on-cancel*
             :is-updating is-updating
             :questionnaire-type questionnaire-type
             :tmp-subject-matter-object tmp-subject-matter-object}

            make-question-props
            (fn [question-type]
              (assoc shared-payload :on-submit (partial persist-question
                                                        question-type
                                                        activity-uuid
                                                        curriculum-uuid
                                                        on-question-submit
                                                        tmp-subject-matter-object)))]
        [:<>
         [text/caption-30 :label "Subject Matter Image"]
         [text/caption-20 :p "Base your question on an image (optional)"]
         [render-file-uploader
          (pot/map-of curriculum-uuid activity-uuid question-uuid
                      question-context tmp-subject-matter-object)]

         (case (-> question-context :question.context/question :question/type keyword)
           :single-answer [single-answer/render (make-question-props :single-answer)]
           :multiple-answer [multiple-answer/render (make-question-props :multiple-answer)]
           :free-response [free-response/render (make-question-props :free-response)]
           :rating-scale [rating-scale/render (make-question-props :rating-scale)]
           (js/console.error "Question type is not supported"))]))))

(defn- render-related-content-step
  [{:keys [curriculum-uuid activity-uuid]}]
  (let [curriculum-resources
        (re-frame/subscribe
         [::kyber-db/get-record
          {:model :curriculum
           :ident curriculum-uuid
           :query [:curriculum/resources]}])]
    (fn [{:keys [is-updating
                 questionnaire-api
                 questionnaire-type
                 questionnaire-uuid
                 question-context
                 on-cancel
                 learning-objectives
                 curriculum-uuid
                 selected-inline-media-uuid
                 toggle-inline-media-selection
                 hide-educator-feedback
                 on-question-submit]
          :as props}]
      [:div
       [:div {:class (:resource-management-container classes)}
        [text/caption-20 :p
         {:style {:margin-bottom (gs [:spacing :p20])}}
         (str "Add Feedback Copy to provide an explanation for the correct "
              "or incorrect answer and/or provide additional guidance.")]

        [text/caption-30 :label "Add Resources"]

        [text/caption-20 :p
         (str "Assign Resource(s) to your Feedback and enable Learners to "
              "explore the topic more deeply and access supportive tools.")]

        (let [add-resource-event
              (:question-ctx-resource-add-event questionnaire-api)

              remove-resource-event
              (:question-ctx-resource-remove-event questionnaire-api)

              on-resource-added
              #(re-frame/dispatch
                [add-resource-event
                 (cond-> {:activity/uuid activity-uuid
                          :question.context/uuid (:question.context/uuid question-context)
                          :resource/uuid (:resource/uuid %)}
                   (= questionnaire-type :floating)
                   (assoc :questionnaire/uuid questionnaire-uuid))
                 {:response-keys (into (kyber-db/get-model-keys :resource)
                                       [:question.context/uuid
                                        :activity/uuid
                                        {:resource/learning-objective
                                         [:learning-objective/uuid
                                          :learning-objective/label
                                          :learning-objective/description]}])
                  :on-success-toast "Resource Added"}])]
          [resource-manager/render
           (cond-> {:on-resource-uploaded on-resource-added
                    :on-resource-selected on-resource-added
                    :on-resource-removed
                    #(re-frame/dispatch
                      [remove-resource-event
                       (cond-> {:activity/uuid activity-uuid
                                :question.context/uuid (:question.context/uuid question-context)
                                :resource/uuid (:resource/uuid %)}
                         (= questionnaire-type :floating)
                         (assoc :questionnaire/uuid questionnaire-uuid))
                       {:response-keys [:activity/uuid]
                        :on-success-toast "Resource Removed"}])
                    :selected-resources (->> question-context
                                             :question.context/resources
                                             (pot/index-by :resource/uuid))
                    :all-resources (:curriculum/resources @curriculum-resources)
                    :learning-objectives learning-objectives
                    :activity-uuid activity-uuid
                    :curriculum-uuid curriculum-uuid
                    :is-updating @is-updating}
             (some? toggle-inline-media-selection)
             (merge
              {:toggle-inline-media-selection toggle-inline-media-selection
               :selected-inline-media-uuid selected-inline-media-uuid}))])]

       [rf/form {:id :question-feedback
                 :is-submitting is-updating}
        [:form
         {:rf/form
          {:on-submit
           #(re-frame/dispatch
             ;; TODO This is far from great. The whole step
             ;;      process for questions and moments needs
             ;;      to be reworked to account for all the
             ;;      change requests that have come in over
             ;;      the past year. A lot of this can be
             ;;      simplified with updated modeling and
             ;;      additional abstractions.
             [::>questions/update-question
              (merge {:question/type (-> question-context :question.context/question :question/type name)
                      :question/uuid (-> question-context :question.context/question :question/uuid)
                      :question/body (-> question-context :question.context/question :question/body)
                      :activity/uuid activity-uuid
                      :curriculum/uuid curriculum-uuid}
                     %)
              {:response-keys (cond->
                               [:question/uuid
                                :question/type
                                :question/body
                                {:question/choices [:question.choice/body
                                                    :question.choice/correct-answer
                                                    :question.choice/uuid]}
                                :question/created-at
                                :question/has-correctness?
                                :question/educator-feedback])
               :on-success-toast (if (nil? (-> question-context :question.context/question :question/uuid))
                                   "Question created."
                                   "Question updated.")
               :on-failure-toast (if (nil? (-> question-context :question.context/question :question/uuid))
                                   "Something went wrong creating this question."
                                   "Something went wrong updating this question.")
               :on-success (fn [updated-question]
                             (let [{:question.context/keys [confidence-enabled? learning-objective]} question-context
                                   close-editor? (-> props should-show-display-settings? not)]
                               (on-question-submit
                                {:is-new false
                                 :close-editor? close-editor?
                                 :learning-objective (:learning-objective/uuid learning-objective)
                                 :confidence confidence-enabled?}
                                updated-question)))}])}}

         (feedback-field/render
          {:hide-educator-feedback hide-educator-feedback
           :educator-feedback (-> question-context :question.context/question :question/educator-feedback)})

         [:div {:class (:cta-container classes)}
          (rf/submit-button
           {:default-text (if (-> props should-show-display-settings? true?)
                            "Save & Continue"
                            "Save & Close")})

          [button/secondary-button
           (cond-> {:on-click on-cancel
                    :style {:margin-left (gs [:spacing :p20])}
                    :type "button"}
             (true? @is-updating)
             (assoc :disabled true))
           "Close"]]]]])))

(defn- render-steps
  [props]
  [:div {:class (:step-container classes)}
   (case (-> props :question-context :step)
     :question [render-question-step props]
     :related-details [render-related-content-step props]
     :display-settings [question-settings/render props]

     (do
       (js/console.error "Invalid question step")
       [:div]))])

(defn- render-step-navigation
  [{:keys [question-context
           on-step-change
           is-updating
           show-related-details-section]
    :as props}]
  (let [is-new (-> question-context :question.context/uuid nil?)
        step (:step question-context)
        {:question.context/keys [learning-objective resources]} question-context
        has-assigned-learning-objective (some? learning-objective)]
    [step-navigation/render
     {:steps
      (filterv some?
               [{:disabled (true? @is-updating)
                 :complete (and (false? is-new)
                                (true? has-assigned-learning-objective))
                 :on-step-change on-step-change
                 :label "Question"
                 :active-step step
                 :step-number "1"
                 :step-key :question}
                (when (true? show-related-details-section)
                  {:disabled (or (true? is-new)
                                 (true? @is-updating))
                   :complete (and (-> resources
                                      (count)
                                      (pos?))
                                  (-> question-context
                                      :question.context/question
                                      :question/educator-feedback
                                      some?))
                   :on-step-change on-step-change
                   :label "Related Details"
                   :active-step step
                   :step-number "2"
                   :step-key :related-details})
                (when (-> props should-show-display-settings? true?)
                  {:disabled (or (true? is-new)
                                 (true? @is-updating))
                   :complete true
                   :on-step-change on-step-change
                   :label "Display Settings"
                   :active-step step
                   :step-number (if (true? show-related-details-section)
                                  "3" "2")
                   :step-key :display-settings})])

      :workflow-title
      (str "Design a " (-> question-context :question.context/question au/question->question-type-display))}]))

(defn render
  [props]
  [:div {:class (:question-editor-container classes)}
   [render-step-navigation props]
   [render-steps props]])
